// apps/web/src/tour/TourGuide.tsx
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTourStore, type TourOutcome } from '@/state/tourStore';
import { TOURS } from '@/tour/messengerTour';
import type { TourStep } from '@/tour/tourTypes';

interface Rect { top: number; left: number; width: number; height: number; }

function useRect(selector: string | undefined): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);
  useEffect(() => {
    if (!selector) { setRect(null); return; }
    let raf = 0;
    let prev: Rect | null = null;
    const sameRect = (a: Rect | null, b: Rect | null) =>
      a === b || (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);
    const measure = () => {
      const el = document.querySelector(selector);
      const next: Rect | null = el
        ? (() => {
            const r = el.getBoundingClientRect();
            return { top: r.top, left: r.left, width: r.width, height: r.height };
          })()
        : null;
      if (!sameRect(prev, next)) {
        prev = next;
        setRect(next);
      }
      raf = requestAnimationFrame(measure);
    };
    measure();
    return () => cancelAnimationFrame(raf);
  }, [selector]);
  return rect;
}

function Ring({ selector, faint, testid }: { selector: string; faint?: boolean; testid: string }) {
  const rect = useRect(selector);
  if (!rect) return null;
  return (
    <div
      data-testid={testid}
      className="tour-ring"
      style={{
        position: 'fixed',
        top: rect.top - 4,
        left: rect.left - 4,
        width: rect.width + 8,
        height: rect.height + 8,
        border: '2px solid #b8862f',
        borderRadius: 12,
        boxShadow: '0 0 0 0 rgba(184,134,47,.5)',
        opacity: faint ? 0.4 : 1,
        pointerEvents: 'none',
        zIndex: 1000,
        animation: 'tour-pulse 1.6s ease-out infinite',
      }}
    />
  );
}

const SUCCESS: Record<TourOutcome, string> = {
  complete: '✓ Deposit sent — you booked it',
  handoff: '✓ Lead handed to the agent queue',
};

/** The docked coach bar: a "Guided demo" chip + the current instruction (or the
 *  completion flash when `done`). One place so the two states can't drift. */
function CoachBar({ text, done }: { text: string; done?: boolean }) {
  return (
    <div
      data-testid="tour-coach"
      aria-live="polite"
      className={done ? 'tour-coach tour-coach--done' : 'tour-coach'}
    >
      <span className="tour-coach__chip" data-testid="tour-coach-chip">Guided demo</span>
      <span className="tour-coach__text">{text}</span>
    </div>
  );
}

export default function TourGuide() {
  const { enabled, activeTourId, activeStepId, lastOutcome, clearOutcome } = useTourStore();

  const tour = activeTourId ? TOURS[activeTourId] : undefined;
  const step: TourStep | undefined = activeStepId ? tour?.steps.find((s) => s.id === activeStepId) : undefined;
  // The coach bar shows for an active step, or briefly for the completion flash.
  const barVisible = enabled && ((!!activeTourId && !!step) || (!activeTourId && !!lastOutcome));

  useEffect(() => {
    if (!lastOutcome) return;
    const t = setTimeout(() => clearOutcome(), 3000);
    return () => clearTimeout(t);
  }, [lastOutcome, clearOutcome]);

  // Reserve space for the docked bar so it never covers app content: the shell
  // root shrinks by --tour-bar-h while `tour-bar-open` is set (see index.css).
  useEffect(() => {
    document.body.classList.toggle('tour-bar-open', barVisible);
    return () => document.body.classList.remove('tour-bar-open');
  }, [barVisible]);

  if (!enabled) return null;

  // Success flash on completion (either ending).
  if (!activeTourId && lastOutcome) {
    return createPortal(<CoachBar text={SUCCESS[lastOutcome]} done />, document.body);
  }

  if (!activeTourId || !step) return null;

  return createPortal(
    <>
      <Ring selector={step.anchor} testid="tour-ring" />
      {step.secondaryAnchor ? (
        <Ring selector={step.secondaryAnchor} faint testid="tour-ring-secondary" />
      ) : null}
      <CoachBar text={step.coach} />
    </>,
    document.body,
  );
}
